import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { Search, ChevronDown, ChevronUp, Sparkles, MapPin, Loader2, Plus, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const matchTagConfig = {
  exact: { label: 'Exact', className: 'bg-green-100 text-green-800 border-green-200' },
  related: { label: 'Related', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  might_help: { label: 'Might Help', className: 'bg-gray-100 text-gray-700 border-gray-200' },
};

// Compact chip used across the two filter rows. Solid variant when active,
// muted outline when inactive. Kept local because the visual language is
// specific to this panel and not reused elsewhere yet.
const FilterChip = ({ active, onClick, children, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition
      ${active
        ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const ResearchPanel = ({ questionText, currentGrade, onClose }) => {
  const [query, setQuery] = useState(questionText || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deepSearching, setDeepSearching] = useState(false);
  const [deepResult, setDeepResult] = useState(null);

  // Filters — reset on every panel mount (no persistence). Grade filter is
  // sent to the backend so ranking works over the correct candidate pool;
  // source filter is applied client-side to the returned top-10.
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'oral' | 'written'
  const [selectedGrades, setSelectedGrades] = useState([]); // [] = All grades
  const [gradesOpen, setGradesOpen] = useState(false);
  const [allGrades, setAllGrades] = useState([]); // fetched grades master list

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const currentQueryRef = useRef(query);
  currentQueryRef.current = query;

  // Load the master grades list once for the multi-select popover.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/grades/config`, { headers });
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : (res.data.items || res.data.grades || []);
        // Show only active grades. `grade_key` is the value stored on
        // question docs (matches what the search backend filters on).
        const names = raw
          .filter(g => (g?.status || 'active') === 'active')
          .map(g => g.grade_key || g.display_name || g)
          .filter(Boolean);
        setAllGrades(names);
      } catch {
        setAllGrades([]);
      }
    })();
    return () => { cancelled = true; };
  }, [headers]);

  const handleSearch = async (searchQuery, gradesOverride) => {
    const q = (searchQuery || currentQueryRef.current || '').trim();
    if (!q || q.length < 3 || loading) return;
    setLoading(true);
    setSearched(true);
    setDeepResult(null);
    try {
      // Pass grades[] to the backend so ranking runs over the correct
      // candidate pool. Empty list == no filter (all grades).
      const body = { query: q };
      const grades = gradesOverride !== undefined ? gradesOverride : selectedGrades;
      if (grades && grades.length > 0) body.grades = grades;
      const res = await axios.post(`${API}/api/ask/search`, body, { headers });
      setResults(res.data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-run initial search when panel opens with a pre-filled question.
  useEffect(() => {
    if (questionText && questionText.trim().length >= 3) {
      setQuery(questionText);
      handleSearch(questionText, selectedGrades);
    }
  }, [questionText]);

  const handleDeepSearch = async () => {
    if (deepSearching) return;
    setDeepSearching(true);
    try {
      const res = await axios.post(`${API}/api/ask/deep-search`, { query }, { headers });
      setDeepResult(res.data);
    } catch {
      setDeepResult({ error: true, message: 'Failed to connect to AI service.' });
    } finally {
      setDeepSearching(false);
    }
  };

  // Client-side source filter — accurate because the backend already
  // returned mixed top-10; toggling this just narrows the render.
  const visibleResults = useMemo(() => {
    if (sourceFilter === 'all') return results;
    return results.filter(r => (r.source_type || 'oral') === sourceFilter);
  }, [results, sourceFilter]);

  const sourceCounts = useMemo(() => {
    const c = { oral: 0, written: 0 };
    results.forEach(r => { c[r.source_type || 'oral'] = (c[r.source_type || 'oral'] || 0) + 1; });
    return c;
  }, [results]);

  // Grade-chip helpers -------------------------------------------------
  const isAllGrades = selectedGrades.length === 0;
  const isOnlyCurrentGrade =
    !!currentGrade && selectedGrades.length === 1 && selectedGrades[0] === currentGrade;

  const applyGrades = (next) => {
    setSelectedGrades(next);
    // Re-run the search immediately so the chip flip feels responsive.
    if (searched) handleSearch(currentQueryRef.current, next);
  };

  const toggleGradeInPopover = (grade) => {
    setSelectedGrades(prev => (
      prev.includes(grade) ? prev.filter(g => g !== grade) : [...prev, grade]
    ));
  };

  const commitPopoverSelection = () => {
    setGradesOpen(false);
    if (searched) handleSearch(currentQueryRef.current, selectedGrades);
  };

  const clearSelectedGrades = () => applyGrades([]);

  // Label for the popover chip when custom grades are picked (excluding the
  // "current grade only" case which has its own dedicated chip).
  const customGradesLabel = useMemo(() => {
    if (isAllGrades || isOnlyCurrentGrade) return null;
    if (selectedGrades.length === 1) return selectedGrades[0];
    return `${selectedGrades.length} grades`;
  }, [selectedGrades, isAllGrades, isOnlyCurrentGrade]);

  return (
    <div className="flex flex-col h-full" data-testid="research-panel">
      {/* Search bar */}
      <div className="flex gap-2 mb-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search similar questions..."
          className="flex-1 text-sm"
          data-testid="research-search-input"
        />
        <Button
          size="sm"
          onClick={() => handleSearch()}
          disabled={loading || query.trim().length < 3}
          className="bg-violet-600 hover:bg-violet-700 shrink-0"
          data-testid="research-search-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </div>

      {/* Filter row 1: source */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-wide text-gray-400 w-14">Source</span>
        <FilterChip active={sourceFilter === 'all'} onClick={() => setSourceFilter('all')} testid="research-src-all">
          All{searched ? ` (${results.length})` : ''}
        </FilterChip>
        <FilterChip active={sourceFilter === 'oral'} onClick={() => setSourceFilter('oral')} testid="research-src-oral">
          Oral{searched ? ` (${sourceCounts.oral || 0})` : ''}
        </FilterChip>
        <FilterChip active={sourceFilter === 'written'} onClick={() => setSourceFilter('written')} testid="research-src-written">
          Written{searched ? ` (${sourceCounts.written || 0})` : ''}
        </FilterChip>
      </div>

      {/* Filter row 2: grade */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide text-gray-400 w-14">Grade</span>
        <FilterChip active={isAllGrades} onClick={() => applyGrades([])} testid="research-grade-all">
          All grades
        </FilterChip>
        {currentGrade && (
          <FilterChip
            active={isOnlyCurrentGrade}
            onClick={() => applyGrades([currentGrade])}
            testid="research-grade-current"
          >
            This grade ({currentGrade})
          </FilterChip>
        )}
        {customGradesLabel && (
          <FilterChip
            active
            onClick={() => setGradesOpen(true)}
            testid="research-grade-custom"
          >
            {customGradesLabel}
            <X className="w-3 h-3 ml-1 opacity-80 hover:opacity-100" onClick={(e) => { e.stopPropagation(); clearSelectedGrades(); }} />
          </FilterChip>
        )}
        <Popover open={gradesOpen} onOpenChange={setGradesOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="research-grade-popover-trigger"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-gray-300 text-[11px] text-gray-500 hover:text-gray-700 hover:border-gray-400"
            >
              <Plus className="w-3 h-3" /> Add grades
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2" data-testid="research-grade-popover">
            <div className="max-h-56 overflow-y-auto pr-1">
              {allGrades.length === 0 && (
                <p className="text-xs text-gray-400 px-1 py-2">No grades available.</p>
              )}
              {allGrades.map(g => (
                <label
                  key={g}
                  className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-gray-50 cursor-pointer text-xs"
                  data-testid={`research-grade-option-${g}`}
                >
                  <Checkbox
                    checked={selectedGrades.includes(g)}
                    onCheckedChange={() => toggleGradeInPopover(g)}
                  />
                  <span className="text-gray-700">{g}</span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-gray-100">
              <button
                type="button"
                onClick={clearSelectedGrades}
                className="text-[11px] text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
              <Button
                size="sm"
                className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
                onClick={commitPopoverSelection}
                data-testid="research-grade-popover-apply"
              >
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {!searched && (
          <p className="text-xs text-gray-400 text-center py-4">
            Search for similar questions to find answers and inspiration.
          </p>
        )}

        {searched && !loading && visibleResults.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-4">
            {results.length === 0
              ? 'No similar questions found.'
              : `No ${sourceFilter} questions in the top matches. Try switching Source back to All.`}
          </p>
        )}

        {loading && (
          <div className="flex items-center gap-2 justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-xs text-gray-500">Searching...</span>
          </div>
        )}

        {visibleResults.map(result => {
          const tag = matchTagConfig[result.match_tag] || matchTagConfig.might_help;
          const isExpanded = expandedId === result.id;
          const isWritten = (result.source_type || 'oral') === 'written';
          return (
            <div
              key={result.id}
              onClick={() => setExpandedId(isExpanded ? null : result.id)}
              className={`p-2.5 rounded-lg border cursor-pointer transition-all text-left ${
                result.match_tag === 'exact' ? 'border-l-2 border-l-green-500' :
                result.match_tag === 'related' ? 'border-l-2 border-l-blue-500' : 'border-l-2 border-l-gray-200'
              } hover:bg-gray-50`}
              data-testid={`research-result-${result.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium text-gray-800 leading-relaxed flex-1">
                  {result.question_text}
                </p>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[10px] text-gray-400">{result.similarity_score}%</span>
                  {isExpanded ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <Badge variant="outline" className={`text-[10px] px-1 py-0 ${tag.className}`}>{tag.label}</Badge>
                <Badge variant="outline" className={`text-[10px] px-1 py-0 ${isWritten ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
                  {isWritten ? 'Written' : 'Oral'}
                </Badge>
                {result.grade && <span className="text-[10px] text-gray-400">{result.grade}</span>}
                {result.function_name && <span className="text-[10px] text-gray-400 break-words min-w-0">{result.function_name}</span>}
                {result.mmd_location && (
                  <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{result.mmd_location}</span>
                )}
              </div>
              {isExpanded && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  {result.answer_text ? (
                    <div>
                      <p className="text-[10px] font-medium text-gray-500 mb-1">Answer</p>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed select-text">
                        {result.answer_text}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No answer available.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Deep Search */}
        {searched && visibleResults.length > 0 && (
          <div className="pt-2 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeepSearch}
              disabled={deepSearching}
              className="text-violet-600 border-violet-200 hover:bg-violet-50 text-xs"
              data-testid="research-deep-search-btn"
            >
              {deepSearching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              Find Deeper (AI)
            </Button>
          </div>
        )}

        {/* Deep Search Result */}
        {deepResult && (
          <div className="mt-2 p-3 rounded-lg border border-violet-200 bg-violet-50/50">
            {deepResult.available === false ? (
              <p className="text-xs text-gray-600">{deepResult.message}</p>
            ) : deepResult.error ? (
              <p className="text-xs text-red-600">{deepResult.message}</p>
            ) : deepResult.rate_limited ? (
              <p className="text-xs text-amber-600">{deepResult.message}</p>
            ) : (
              <div>
                <div className="flex items-center gap-1 mb-1.5">
                  <Sparkles className="w-3 h-3 text-violet-500" />
                  <span className="text-[10px] font-medium text-violet-600">AI Deep Search</span>
                </div>
                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed select-text">
                  {deepResult.answer}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResearchPanel;
